import { useEffect, useState } from 'react'

import { useNotification } from '@/Core/Hooks/useNotification'

import { CompleteTaskModal } from '../Components/Modals/CompleteTaskModal'
import { getErrorMessage } from '../Helpers/getErrorMessage'
import { CompleteTaskModalStore } from '../Stores/CompleteTaskModalStore'
import { RestoreTaskTask } from '../Tasks/RestoreTaskTask'
import { StartTaskTask } from '../Tasks/StartTaskTask'
import { SuspendTaskTask } from '../Tasks/SuspendTaskTask'

import type { TaskCrudContextLike } from '../Contracts/Crud.types'

interface Props {
    crudContext: TaskCrudContextLike
}

/**
 * Монтується у `content` через `onButtons` картки (SPEC-008 §8.3, автопатерн
 * autostrada `RequestViewActions`). Тримає стор `CompleteTaskModal` і слухає події
 * воркфлоу, які штовхають кнопки:
 *   • `taskAction:start`            → `StartTaskTask` + reload/notify
 *   • `taskAction:suspend`          → `SuspendTaskTask` + reload/notify
 *   • `taskAction:openCompleteModal`→ відкриває модалку завершення
 *   • `taskAction:restore`          → `RestoreTaskTask` + reload/notify
 * notify/reload живуть тут (React-компонент з хуками), а не в `onClick` кнопки.
 */
export const TaskViewActions = ({ crudContext }: Props) => {
    const { showSuccess, showError } = useNotification()
    const [store] = useState(() => new CompleteTaskModalStore())

    useEffect(() => {
        const em = crudContext?.eventManager
        if (!em) return

        const uuidOf = (): string | undefined => crudContext?.formContext?.values?.uuid
        const reload = (): void => crudContext?.actions?.reloadData?.()

        em.on('taskAction:start', 'taskStartHandler', () => {
            const uuid = uuidOf()
            if (!uuid) return
            StartTaskTask(uuid)
                .then(() => {
                    showSuccess('Завдання запущено')
                    reload()
                })
                .catch((e: unknown) => showError(getErrorMessage(e) || 'Не вдалося запустити завдання'))
        })

        em.on('taskAction:suspend', 'taskSuspendHandler', () => {
            const uuid = uuidOf()
            if (!uuid) return
            SuspendTaskTask(uuid)
                .then(() => {
                    showSuccess('Завдання призупинено')
                    reload()
                })
                .catch((e: unknown) => showError(getErrorMessage(e) || 'Не вдалося призупинити завдання'))
        })

        em.on('taskAction:restore', 'taskRestoreHandler', () => {
            const uuid = uuidOf()
            if (!uuid) return
            RestoreTaskTask(uuid)
                .then(() => {
                    showSuccess('Завдання повернуто')
                    reload()
                })
                .catch((e: unknown) => showError(getErrorMessage(e) || 'Не вдалося повернути завдання'))
        })

        em.on('taskAction:openCompleteModal', 'taskCompleteHandler', () => {
            store.openFor(crudContext?.formContext?.values ?? {})
        })

        return () => {
            em.off('taskAction:start', 'taskStartHandler')
            em.off('taskAction:suspend', 'taskSuspendHandler')
            em.off('taskAction:openCompleteModal', 'taskCompleteHandler')
        }
    }, [crudContext, store, showSuccess, showError])

    return <CompleteTaskModal store={store} crudContext={crudContext} />
}
